/**
 * Chart→experiment pen sync, as a pure reconciliation.
 *
 * THE INVARIANT: the viewer's pens and the experiment's tag set
 * (target + drivers) are the same set of tags, converged from both sides:
 *
 * - target = the first tag to arrive, never auto-overwritten while its pen
 *   is on the chart (choosing a different target is a deliberate act in the
 *   tab). Removing the target's pen clears the target; drivers are NOT
 *   auto-promoted, and the next new pen becomes the target.
 * - every other pen is a driver, in pen order (existing driver order is
 *   preserved; new pens append).
 * - a pen removed from the chart leaves the experiment; a tag removed in
 *   the tab drops its pen (that direction lives in ExperimentLabContext).
 *
 * Reconciliation (state-based) rather than diff-based on purpose: the old
 * add/remove diffing kept a "previous pens" baseline and re-baselined on
 * multi-pen changes, which silently swallowed legitimate adds — pens already
 * on the chart when the provider mounted never imported, and a user add
 * landing in the same commit as a chart restore read as a "bulk" change and
 * was dropped. Reconciling against the current pen list is idempotent and
 * immune to event ordering: whatever sequence produced the pens, the config
 * converges to match them.
 *
 * Known edge: a config tag whose pen could not be added (tag missing from
 * this site's sensor pool, e.g. a saved experiment from another site) is
 * reconciled OUT of the config on the next pen change — tags without pens
 * don't survive, by the invariant.
 */
export function reconcileConfigTagsWithPens(
  target: string,
  drivers: string[],
  penIds: string[]
): { target: string; drivers: string[] } | null {
  const penSet = new Set(penIds)

  const nextTarget = target && penSet.has(target) ? target : ''
  const keptDrivers = drivers.filter(
    (driver) => penSet.has(driver) && driver !== nextTarget
  )

  const known = new Set(keptDrivers)
  if (nextTarget) known.add(nextTarget)

  let finalTarget = nextTarget
  const nextDrivers = [...keptDrivers]
  for (const penId of penIds) {
    if (known.has(penId)) continue
    known.add(penId)
    if (!finalTarget) finalTarget = penId
    else nextDrivers.push(penId)
  }

  const unchanged =
    finalTarget === target &&
    nextDrivers.length === drivers.length &&
    nextDrivers.every((driver, index) => driver === drivers[index])
  return unchanged ? null : { target: finalTarget, drivers: nextDrivers }
}
